import {
  DEFAULT_ICON_PICKER_VALUE,
  isIconColorName,
  isIconName,
} from '@buffer-mono/popcorn'
import type { IconName, IconPickerValue } from '@buffer-mono/popcorn'

import type { SavedView } from './hooks'

/**
 * Icon names that were renamed or dropped from the picker in a past resync.
 * Persisted saved views may still store them, so map each to its nearest
 * current glyph rather than letting it fall back to the default. The stored
 * value is untouched — this only affects what renders.
 */
const LEGACY_ICON_ALIASES: Record<string, IconName> = {
  'tree-pine': 'tree',
  'suitcase-alt': 'suitcase',
}

/**
 * Resolves a persisted icon string to a current {@link IconName}, tolerating
 * values from an older icon set: a renamed name resolves to its replacement, an
 * unrecognized one to the default.
 */
function resolveIcon(icon: string | null): IconName {
  if (icon === null) return DEFAULT_ICON_PICKER_VALUE.icon
  if (isIconName(icon)) return icon
  return LEGACY_ICON_ALIASES[icon] ?? DEFAULT_ICON_PICKER_VALUE.icon
}

/**
 * Resolves a saved view's persisted icon/color into a valid
 * {@link IconPickerValue}.
 *
 * `icon` and `color` arrive from GraphQL as nullable, unconstrained strings.
 * They may be null (never set) or name an icon/color that is no longer offered.
 * Each field is narrowed and falls back to the default independently, so a
 * stale color doesn't discard a still-valid icon (and vice versa); a renamed
 * icon is mapped to its current equivalent via {@link LEGACY_ICON_ALIASES}.
 */
export function savedViewIdentity(
  view: Pick<SavedView, 'icon' | 'color'>,
): IconPickerValue {
  return {
    icon: resolveIcon(view.icon),
    color:
      view.color !== null && isIconColorName(view.color)
        ? view.color
        : DEFAULT_ICON_PICKER_VALUE.color,
  }
}
